import { PencilIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { DocResponse } from '../../../server/response/responseScheme';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import { MarkdownEditor, MarkdownEditorRef } from './editor/MarkdownEditor';
import { MarkdownViewer } from './editor/MarkdownViewer';

class ViewStore {
    docKey: string;
    doc: DocResponse | null = null;
    isLoading = false;

    constructor(docKey: string) {
        this.docKey = docKey;
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        const result = await apiClient.doc.public.getDocByKey.$query({
            docKey: this.docKey,
        });

        runInAction(() => {
            this.doc = result;
            this.isLoading = false;
        });
    }

    async save(content: string) {
        const result = await apiClient.doc.admin.updateDoc.mutate({
            docKey: this.docKey,
            content,
        });
        apiClient.doc.public.getDocByKey.$setCache({ docKey: this.docKey }, result);

        runInAction(() => {
            this.doc = result;
        });
    }
}

/**
 * 문서 뷰어
 * - doc 테이블의 마크다운 컨텐츠를 마크다운 뷰어로 보여줌
 */
const DocViewer = observer(({ docKey }: { docKey: string }) => {
    const viewStore = useMobxInstance(ViewStore, docKey);
    const [isEditing, setIsEditing] = useState(false);
    const editorRef = useRef<MarkdownEditorRef>(null);

    useEffect(() => {
        viewStore.fetch();
    }, [docKey]);

    useEffect(() => {
        if (isEditing && viewStore.doc?.content) {
            editorRef.current?.setInitialContent({
                content: viewStore.doc.content,
            });
        }
    }, [isEditing, viewStore.doc]);

    let content = viewStore.doc?.content || '';

    if (isEditing) {
        return (
            <MarkdownEditor
                ref={editorRef}
                useTitle={false}
                contentMinRows={3}
                contentMaxRows={20}
                onClickSave={async ({ content }) => {
                    await viewStore.save(content);
                    setIsEditing(false);
                }}
            />
        );
    }

    return (
        <div className="relative min-h-20">
            {viewStore.isLoading && (
                <div className="flex h-20 w-full items-center justify-center">
                    <div className="loading loading-spinner loading-lg text-base-content/30" />
                </div>
            )}
            {!viewStore.isLoading && (
                <MarkdownViewer
                    markdown={content}
                    components={{
                        // 제목과 본문 여백을 추가
                        h3: ({ children }) => <h3 className="mb-5">{children}</h3>,
                    }}
                />
            )}
            {import.meta.env.DEV && (
                <button
                    className="btn btn-circle btn-ghost btn-xs absolute top-1 right-1"
                    onClick={() => setIsEditing(true)}
                >
                    <PencilIcon className="text-base-content/50 size-4" />
                </button>
            )}
        </div>
    );
});

export default DocViewer;
